import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuditProvider } from "./context/AuditContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Phase1Context from "./pages/Phase1Context";
import Phase2Documents from "./pages/Phase2Documents";
import Phase3Controls from "./pages/Phase3Controls";
import Phase4Scope from "./pages/Phase4Scope";
import Phase5Artifacts from "./pages/Phase5Artifacts";
import Phase6Sampling from "./pages/Phase6Sampling";
import Phase7EvidenceSqlControls from "./pages/Phase7EvidenceSqlControls";
import Phase7EvidencePreview from "./pages/Phase7EvidencePreview";
import Phase8Findings from "./pages/Phase8Findings";
import Phase9Classification from "./pages/Phase9Classification";
import Phase10Report from "./pages/Phase10Report";
import ModelConfig from "./pages/ModelConfig";
import AuditLogs from "./pages/AuditLogs";
import UserManagement from "./pages/UserManagement";
import Profile from "./pages/Profile";
import Integrations from "./pages/Integrations";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children, roles, permissions }) {
  const { isAuthenticated, loading, user, hasAnyPermission } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && user && !roles.includes(user.role)) return <Navigate to="/phase/1" replace />;
  if (permissions && !hasAnyPermission(...permissions)) return <Navigate to="/phase/1" replace />;
  return children;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/phase/1" replace /> : <Login />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/phase/1" replace />} />
        <Route path="phase/1" element={<Phase1Context />} />
        <Route path="phase/2" element={<Phase2Documents />} />
        <Route path="phase/3" element={<Phase3Controls />} />
        <Route path="phase/4" element={<Phase4Scope />} />
        <Route path="phase/5" element={<Phase5Artifacts />} />
        <Route path="phase/6" element={<Phase6Sampling />} />
        <Route path="phase/7" element={<Phase7EvidenceSqlControls />} />
        <Route path="phase/7-preview" element={<Phase7EvidencePreview />} />
        <Route path="phase/8" element={<Phase8Findings />} />
        <Route path="phase/9" element={<Phase9Classification />} />
        <Route path="phase/10" element={<Phase10Report />} />
        <Route path="profile" element={<Profile />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="config" element={<ProtectedRoute permissions={["model:config"]}><ModelConfig /></ProtectedRoute>} />
        <Route path="logs" element={<ProtectedRoute permissions={["logs:view"]}><AuditLogs /></ProtectedRoute>} />
        <Route path="users" element={<ProtectedRoute permissions={["users:manage"]}><UserManagement /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/phase/1" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AuditProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuditProvider>
    </AuthProvider>
  );
}

export default App;
